// @flow

import * as React from 'react';
import { head } from 'lodash/fp';
import styled from '@emotion/styled';
import Button from 'app/components/commons/Button';
import InputField from 'app/components/commons/InputField';
import Spinner from 'app/components/commons/Spinner';
import { fileToDataUrl } from 'app/utils/fileUtils';
import DocumentDropZoneField from './DocumentDropZoneField';

type Props = {
  availableBalance: number,
  currencyCode: string,
  currencySymbol: string,
  fields: *,
  fullName: string,
  isSubmitting?: boolean,
  lightContent?: boolean,
  onClick: (withdrawalInfo: Object) => void,
  withdrawalDetails: ?{
    id: number,
    label: string,
    status: {
      id: number,
      name: string
    }
  }
};

type File = {
  preview: Image,
  name: string
};

type State = {
  acceptedFile?: File,
  isFileRejected: boolean,
  fieldValues: {
    account_name: string,
    account_number: string,
    amount: number,
    bsb?: string,
    bank_name?: string,
    swift_code?: string
  }
};

const ExistingAccountDiv = styled.div({
  alignItems: 'center',
  display: 'flex'
});

const ExistingAccountIcon = styled.div(({ theme }) => ({
  color: theme.color.primary,
  fontSize: theme.fontSize.xlarge,
  marginRight: theme.spacing.small
}));

const ExistingAccountData = styled.div(({ theme }: { theme: Theme }) => ({
  color: theme.color.basis4
}));

class WithdrawForm extends React.Component<Props, State> {
  props: Props;

  state: State;

  static defaultProps = {
    isSubmitting: false,
    lightContent: false
  };

  constructor(...args: Array<any>) {
    super(...args);

    const { fields, fullName } = this.props;
    const defaultFields = {
      account_name: fullName,
      account_number: '',
      amount: 10
    };

    this.state = {
      isFileRejected: false,
      fieldValues: {
        ...defaultFields,
        ...fields.reduce(
          (memo, field) => ({
            ...memo,
            [field.key]: ''
          }),
          {}
        )
      }
    };
  }

  handleInputChange(key: string, value: string | number) {
    const { availableBalance } = this.props;

    if (key === 'amount' && (value > availableBalance || value < 0)) {
      return;
    }

    const { fieldValues } = this.state;
    this.setState({
      fieldValues: Object.assign({}, fieldValues, {
        [key]: value
      })
    });
  }

  handleButtonClick = () => {
    const { onClick } = this.props;
    const { fieldValues } = this.state;

    this.getWithdrawalInfo().then(withdrawalInfo => {
      onClick(withdrawalInfo);

      const newFieldValues = { ...fieldValues, amount: 0 };
      this.setState({ fieldValues: newFieldValues });
    });
  };

  handleDocumentDrop = (accepted: Array<Object>, rejected: Array<Object>) => {
    const file = head(accepted || []);

    this.setState({
      acceptedFile: file
        ? Object.assign(file, {
            preview: URL.createObjectURL(file)
          })
        : undefined,
      isFileRejected: !!head(rejected)
    });
  };

  getWithdrawalInfo(): Promise<any> {
    const { withdrawalDetails } = this.props;
    const { acceptedFile, fieldValues } = this.state;

    return new Promise(resolve => {
      if (withdrawalDetails) {
        resolve({
          amount: fieldValues.amount,
          payment_id: withdrawalDetails.id
        });
      } else {
        fileToDataUrl(acceptedFile).then(dataUrl =>
          resolve({
            document: dataUrl,
            ...fieldValues
          })
        );
      }
    });
  }

  render() {
    const {
      availableBalance,
      currencyCode,
      currencySymbol,
      fields,
      isSubmitting,
      lightContent,
      withdrawalDetails
    } = this.props;
    const { acceptedFile, fieldValues, isFileRejected } = this.state;

    return (
      <form>
        <InputField
          label={`Amount to withdraw (${currencyCode})`}
          type="number"
          min={0}
          max={availableBalance}
          name="amount"
          placeholder="0.00"
          helper={currencySymbol}
          value={fieldValues.amount}
          onChange={value => this.handleInputChange('amount', value)}
          lightContent={lightContent}
        />
        <hr />
        {withdrawalDetails ? (
          <div className="form-field-group">
            <div className="form-input-label">Bank account details</div>
            <ExistingAccountDiv>
              <ExistingAccountIcon>
                <span className="ion-ios-checkmark" />
              </ExistingAccountIcon>
              <ExistingAccountData>{withdrawalDetails.label}</ExistingAccountData>
            </ExistingAccountDiv>
            <hr />
          </div>
        ) : (
          <div>
            {fields.map(field => (
              <InputField
                key={field.key}
                label={field.label}
                name={field.key}
                type={field.type}
                placeholder={field.placeholder}
                value={fieldValues[field.key]}
                onChange={value => this.handleInputChange(field.key, value)}
                lightContent={lightContent}
              />
            ))}
            <DocumentDropZoneField
              onDrop={this.handleDocumentDrop}
              acceptedFile={acceptedFile}
              displayRejectedMessage={isFileRejected}
            />
          </div>
        )}
        <div className="form-button">
          <Button block size="large" disabled={isSubmitting} onClick={this.handleButtonClick}>
            {isSubmitting ? (
              <div className="buttonInner">
                <Spinner />
                <span className="buttonLabel">Withdrawing...</span>
              </div>
            ) : (
              <div>Withdraw Funds</div>
            )}
          </Button>
        </div>
        <div className="form-notes text-center">
          Please allow up to 5 business days for your request to be processed
        </div>
      </form>
    );
  }
}

export default WithdrawForm;
